<script>
/**
 * @component TreatmentDetail
 * @description Formulario de creación/edición de protocolo de tratamiento.
 */
import treatmentService from "@/services/treatment";
import segmentService from "@/services/segment";
import medicalSpecialtyService from "@/services/medical-specialty";

import Spinner from "@/components/ui/Spinner";

export default {
  name: "TreatmentDetail",

  components: {
    Spinner,
  },

  data() {
    return {
      treatmentId: this.$route.params.id,
      treatment: {},
      isUpdate: this.$route.name === "update-treatment",
      isFetchingTreatment: false,
      isPostingTreatment: false,
      medicalSpecialties: [],
    };
  },

  created() {
    this.getTreatment();
    this.getMedicalSpecialties();
  },

  mounted() {
    document.addEventListener("keyup", this.escape);
  },

  beforeDestroy() {
    document.removeEventListener("keyup", this.escape);
  },

  methods: {
    escape(event) {
      if (event.keyCode == 27) this.goToRoute("treatment");
    },

    submit() {
      this.createOrUpdateTreatment();
    },

    createOrUpdateTreatment() {
      const eventName = this.isUpdate
        ? "Treatment Updated"
        : "Treatment Created";
      segmentService.track({ name: eventName });

      this.isPostingTreatment = true;
      const serviceCall = this.isUpdate
        ? treatmentService.update
        : treatmentService.create;

      return serviceCall(this.treatment)
        .then((treatment) => {
          this.treatmentId = treatment._id;
          this.$bus.$emit("treatment-update", treatment || this.treatment);
        })
        .then(() => {
          if (this.isUpdate) {
            this.goToRoute("treatment");
          } else {
            this.goToRoute("treatment-tasks", this.treatmentId);
          }
        })
        .finally(() => {
          this.isPostingTreatment = false;
        });
    },

    getTreatment() {
      if (this.isUpdate) {
        this.isFetchingTreatment = true;
        treatmentService
          .getById(this.treatmentId)
          .then((treatment) => {
            this.treatment = treatment;
          })
          .finally(() => {
            this.isFetchingTreatment = false;
          });
      }
    },

    async getMedicalSpecialties() {
      const medicalSpecialties =
        await medicalSpecialtyService.getMedicalSpecialties();
      this.medicalSpecialties = medicalSpecialties;
    },

    goToRoute(name, id = "") {
      this.$router.push({ name, params: { id } });
    },

    /**
     * @description Devuelve el título del formulario según contexto
     * @returns {string}
     */
    getButtonName() {
      if (this.isUpdate) {
        return "Actualizar Protocolo";
      }
      return "Nuevo Protocolo";
    },
  },
};
</script>

<template lang="pug">
section.treatment-detail-page
  //- Nav header
  .detail-nav
    button.btn-back(@click="goToRoute('treatment')")
      i.el-icon-arrow-left
    h1.detail-nav__title {{ getButtonName() }}

  //- Form card
  ValidationObserver(v-slot="{ handleSubmit }")
    form.detail-card(@submit.prevent="handleSubmit(submit)")
      .detail-card__header
        h2 Información del protocolo

      .detail-card__body(v-if="isFetchingTreatment")
        spinner

      .detail-card__body(v-else)
        .form-group
          label.form-label * Nombre del protocolo
          ValidationProvider(name="Nombre", rules="required", v-slot="{ errors }")
            input.form-input(
              v-model="treatment.name"
              placeholder="Ej: Protocolo de seguimiento post-cirugía"
              autofocus
            )
            span.form-error(v-if="errors[0]") {{ errors[0] }}

        .form-group
          label.form-label Descripción
          textarea.form-textarea(
            v-model="treatment.description"
            placeholder="Descripción del protocolo..."
            rows="6"
          )

        .form-group
          label.form-label Especialidad
          el-select.form-select(
            v-model="treatment.medicalSpecialty"
            placeholder="Seleccione especialidad"
            filterable
            clearable
          )
            el-option(
              v-for="medicalSpecialty in medicalSpecialties"
              :key="medicalSpecialty._id"
              :value="medicalSpecialty._id"
              :label="medicalSpecialty.name"
            )

      .detail-card__footer
        button.btn-secondary(type="button" @click="goToRoute('treatment')") Cancelar
        button.btn-primary(type="submit" :disabled="isPostingTreatment")
          span(v-if="!isPostingTreatment") Guardar
          span(v-else) Guardando...
</template>

<style lang="scss" scoped>
.treatment-detail-page {
  max-width: 640px;
  margin: 0 auto;
}

/* ── Nav ── */
.detail-nav {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 20px;
}

.btn-back {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;

  i {
    font-size: 16px;
    color: #002d5e;
  }

  &:hover {
    border-color: #002d5e;
  }
}

.detail-nav__title {
  font-size: 20px;
  font-weight: 700;
  color: #002d5e;
  margin: 0;
}

/* ── Card ── */
.detail-card {
  background: #fff;
  border-radius: 12px;
  border: 1.5px solid #e2e8f0;
  overflow: hidden;
}

.detail-card__header {
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;

  h2 {
    font-size: 18px;
    font-weight: 700;
    color: #002d5e;
    margin: 0;
  }
}

.detail-card__body {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.detail-card__footer {
  padding: 16px 24px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* ── Form fields ── */
.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-label {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.form-input {
  padding: 10px 14px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
  font-size: 14px;
  font-family: "Plus Jakarta Sans", sans-serif;
  color: #002d5e;
  outline: none;
  transition: all 0.2s;

  &:focus {
    background: #fff;
    border-color: #002d5e;
    box-shadow: 0 0 0 3px rgba(0, 45, 94, 0.08);
  }

  &::placeholder {
    color: #cbd5e1;
  }
}

.form-textarea {
  padding: 10px 14px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
  font-size: 14px;
  font-family: "Plus Jakarta Sans", sans-serif;
  color: #002d5e;
  outline: none;
  transition: all 0.2s;
  resize: vertical;
  min-height: 100px;

  &:focus {
    background: #fff;
    border-color: #002d5e;
    box-shadow: 0 0 0 3px rgba(0, 45, 94, 0.08);
  }

  &::placeholder {
    color: #cbd5e1;
  }
}

.form-error {
  font-size: 12px;
  color: #dc2626;
}

/* ── Buttons ── */
.btn-secondary {
  display: inline-flex;
  align-items: center;
  padding: 10px 22px;
  background: #fff;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  font-family: "Plus Jakarta Sans", sans-serif;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #94a3b8;
    color: #002d5e;
  }
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  padding: 10px 22px;
  background: #002d5e;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  font-family: "Plus Jakarta Sans", sans-serif;
  color: #fff;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #003d7a;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}
</style>

<style lang="scss">
/* Override Element UI select inside treatment detail */
.treatment-detail-page {
  .el-select {
    width: 100%;
  }

  .el-input__inner {
    border: 1.5px solid #e2e8f0;
    border-radius: 10px;
    background: #f8fafc;
    font-size: 14px;
    font-family: "Plus Jakarta Sans", sans-serif;
    color: #002d5e;
    padding: 10px 14px;
    height: auto;

    &:focus {
      background: #fff;
      border-color: #002d5e;
      box-shadow: 0 0 0 3px rgba(0, 45, 94, 0.08);
    }

    &::placeholder {
      color: #cbd5e1;
    }
  }
}
</style>
